import { useAuthStore } from "@/stores/authStore"
import { api } from "@/requests/config"
import { useShop } from "@/context/shop-context"
import { Item, ItemFormValues, ItemTag } from "./schema"

interface PaginatedResponse<T> {
    total_count: number
    per_page: number
    page: number
    data: T[]
}

export type ItemLiteRow = { id: string; label: string }
export type TagListResponse = { data: ItemTag[] }

export async function getItemsLiteByShop(
    shopId: string,
    accessToken: string,
): Promise<ItemLiteRow[]> {
    const response = await api.get<{ data: { items: ItemLiteRow[] } }>(
        `items/shop/${shopId}/lite`,
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
        },
    )
    const items = response.data?.data?.items
    return Array.isArray(items) ? items : []
}

export const fetchItemsByShop = () => {
    const { accessToken } = useAuthStore((state) => state.auth)
    const { activeShop } = useShop()

    const items = (): Promise<any> =>
        api
            .get(`items/shop/${activeShop?._id}?list_all=true`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            })
            .then((response: any) => response.data.data)

    return items
}

export async function fetchTags(): Promise<ItemTag[]> {
    const accessToken = useAuthStore.getState().auth.accessToken
    const response = await api.get<TagListResponse>(`/tags?list_all=true`, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
    })
    return Array.isArray(response.data?.data) ? response.data.data : []
}

export const getItemsByShop = async (
    shopId: string,
    page: number,
    perPage: number
): Promise<PaginatedResponse<Item>> => {
    const accessToken = useAuthStore.getState().auth.accessToken
    return api
        .get(`items/shop/${shopId}`, {
            params: { page, per_page: perPage },
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
        })
        .then((response: any) => response.data)
}

export const updateItemOrder = async (list: {
    id: string
    order: number
}[], accessToken: string) => {
    
    const data = await api.put<Item>(`/items/order`, list, {
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
    }).then((response: any) => response.data.data)
    return data
}


export async function createItem(values: ItemFormValues, accessToken: string) {

    const data = await api.post<Item>('/items', values, {
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
    }).then((response: any) => response.data.data)
    return data
}

/** API / wire shape — snake_case */
export type BatchItemInput = {
    category_id: string
    name: Record<string, string>
    description?: Record<string, string>
    price: number
    files?: ItemFormValues['files']
    tags?: string[]
    status?: 'ACTIVE' | 'PASSIVE' | 'DELETED'
}

export type BatchCreateItemsPayload = {
    shop_id: string
    items: BatchItemInput[]
}

export type BatchItemCreatedRow = {
    index: number
    item: Item
}

export type BatchItemFailedRow = {
    index: number
    message: string
}

export type BatchCreateItemsResponse = {
    created: BatchItemCreatedRow[]
    failed: BatchItemFailedRow[]
}

export async function batchCreateItems(
    payload: BatchCreateItemsPayload,
    accessToken: string,
): Promise<BatchCreateItemsResponse> {
    const data = await api
        .post<{ data: BatchCreateItemsResponse }>('/items/batch', payload, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
        })
        .then((response) => response.data.data)
    return data
}

export async function updateItem(id: string, values: ItemFormValues, accessToken: string) {
    const data = await api.put<Item>(`/items/${id}`, values, {
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
    }).then((response: any) => response.data.data)
    return data
}

export async function deleteItem(id: string, accessToken: string) {
    await api.delete(`/items/${id}`, {
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
    })
}

/** API / wire shape — snake_case */
export type BulkItemsPayload = {
    shop_id: string
    ids: string[]
}

export async function bulkDeleteItems(
    shopId: string,
    ids: string[],
    accessToken: string,
): Promise<{ affected_count: number }> {
    const data = await api
        .post<{ data: { affected_count: number } }>(
            '/items/bulk-delete',
            { shop_id: shopId, ids },
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            },
        )
        .then((response) => response.data.data)
    return data
}

export async function bulkUpdateItemsStatus(
    shopId: string,
    ids: string[],
    status: 'ACTIVE' | 'PASSIVE',
    accessToken: string,
): Promise<{ affected_count: number }> {
    const data = await api
        .post<{ data: { affected_count: number } }>(
            '/items/bulk-status',
            { shop_id: shopId, ids, status },
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            },
        )
        .then((response) => response.data.data)
    return data
}


